import React from 'react';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { IconButton, Slider } from '@mui/material';

import { Box, Stack, Typography } from '../layout';

export interface EliceVideoPlayerProps {
  source: string;
  autoPlay?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onProgress?: (seconds: number) => void;
  tracks?: { src: string; kind: string; srclang: string; label: string }[];
  sx?: React.CSSProperties;
  height?: string;
  pageKey?: string;
  videoKey?: string;
}

const EliceVideoPlayer = ({
  source,
  autoPlay = false,
  onPlay,
  onPause,
  onEnded,
  onProgress,
  tracks,
  sx,
  height = 'auto',
  videoKey,
}: EliceVideoPlayerProps) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const lastTime = React.useRef<number>(
    videoKey ? Number(localStorage.getItem(videoKey)) || 0 : 0
  );

  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(lastTime.current);
  const [duration, setDuration] = React.useState(0);
  const [volume, setVolume] = React.useState(1);
  const [speed, setSpeed] = React.useState(1);
  const [isMuted, setIsMuted] = React.useState(false);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = lastTime.current;

    const handlePlay = () => {
      setIsPlaying(true);
      onPlay?.();
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPause?.();
    };

    const handleEnded = () => {
      setIsPlaying(false);
      onEnded?.();
    };

    const handleTimeUpdate = () => {
      const seconds = video.currentTime;
      setCurrentTime(seconds);
      onProgress?.(seconds);

      if (videoKey) {
        localStorage.setItem(videoKey, seconds.toString());
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };
    const handleFullscreenChange = () => {
      const fullscreenElement = videoRef.current?.parentElement?.parentElement;
      if (fullscreenElement === document.fullscreenElement) {
        setIsFullscreen(true);
      } else {
        document.exitFullscreen()?.catch(error => {
          console.warn("Could not exit fullscreen:", error.message);
        });
        setIsFullscreen(false);
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [videoKey, onPlay, onPause, onEnded, onProgress]);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        void video.play();
      }
    }
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    const volume = newValue as number;
    setVolume(volume);
    const video = videoRef.current;
    if (video) {
      video.volume = volume;
    }
  };

  const handleSpeedChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const speed = event.target.value as number;
    setSpeed(speed);
    const video = videoRef.current;
    if (video) {
      video.playbackRate = speed;
    }
  };

  const toggleMute = () => {
    setIsMuted(prev => {
      const video = videoRef.current;
      if (video) {
        video.muted = !prev;
        return !prev;
      }
      return prev;
    });
  };

  const toggleFullscreen = () => {
    const videoContainer = videoRef.current?.parentElement?.parentElement;
    if (videoContainer) {
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(error => {
          console.warn("Could not exit fullscreen:", error.message);
        });
      } else {
        void videoContainer.requestFullscreen?.();
      }
    }
  };

  const handleSeek = (newValue: number, isInteraction: boolean) => {
    const newTime = newValue;
    const video = videoRef.current;
    if (isInteraction && video) {
      video.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  return (
    <Stack
      step={2}
      width="100%"
      height={height}
      borderRadius="1rem"
      overflow="hidden"
      border="1px solid #e0e0e0"
      sx={{
        '& video': {
          width: '100%',
          height: '100%',
          borderTopRadius: '1rem',
          objectFit: 'cover',
        },
        ...sx,
      }}
    >
      <Box
        sx={{ flex: '1 1 auto', overflow: 'hidden', position: 'relative' }}
      >
        <video
          ref={ref => {
            if (isPlaying) {
              void ref?.play();
            }
            videoRef.current = ref;
          }}
          controls={false}
          autoPlay={autoPlay}
          width="100%"
          crossOrigin="anonymous"
        >
          <source src={source} type="video/mp4" />
          {tracks?.map((track, i) => (
            <track
              key={i}
              src={track.src}
              kind={track.kind}
              srcLang={track.srclang}
              label={track.label}
            />
          ))}
          Your browser does not support the video tag.
        </video>
      </Box>
      {/* Custom Controls */}
      <Stack
        direction="row"
        height="max(30px, 3em)"
        backgroundColor="black"
        alignItems="center"
        justifyContent="space-between"
        padding="0.5em 1em"
        gap="0.5em"
      >
        {/* Play/Pause Button */}
        <IconButton onClick={handlePlayPause} color="primary">
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        {/* Current Time */}
        <Typography width="2.5em" color="white">
          {`${Math.floor(currentTime / 60)}:${String(
            Math.floor(currentTime % 60)
          ).padStart(2, '0')}`}
        </Typography>

        {/* Progress Bar */}
        <Box flex="1 1" mx="0.5em" width="10em">
          <Seekbar
            value={currentTime}
            max={duration}
            onChange={handleSeek}
            trackColor="#e0e0e0"
            progressBarColor="primary.main"
            videoRef={videoRef}
          />
        </Box>

        {/* Video Duration */}
        <Typography width="2.5em" color="white">
          {`${Math.floor(duration / 60)}:${String(
            Math.floor(duration % 60)
          ).padStart(2, '0')}`}
        </Typography>

        {/* Speed Control */}
        <select
          onChange={handleSpeedChange}
          value={speed}
          style={{
            backgroundColor: 'black',
            color: 'white',
            borderRadius: '0.25em',
            border: '1px solid #e0e0e0',
            padding: '0 0.5em',
          }}
        >
          <option value={0.5}>0.5x</option>
          <option value={1}>1x</option>
          <option value={1.5}>1.5x</option>
          <option value={2}>2x</option>
        </select>

        {/* Volume Control */}
        <Stack
          direction="row"
          justifyContent="space-around"
          alignItems="center"
          width="6em"
          gap="0.5em"
        >
          <IconButton onClick={toggleMute} color="primary">
            {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>
          <Slider
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            min={0}
            max={1}
            step={0.01}
          />
        </Stack>

        {/* Subtitles Toggle */}
        <IconButton color="primary">
          <SubtitlesIcon />
        </IconButton>

        {/* Fullscreen Toggle */}
        <IconButton onClick={toggleFullscreen} color="primary">
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Stack>
    </Stack>
  );
};

export default EliceVideoPlayer;

interface SeekbarProps {
  videoRef?: React.RefObject<HTMLVideoElement>;
  value: number;
  onChange: (newValue: number, isUserInteraction: boolean) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  trackColor?: string; // 트랙 색상
  progressBarColor?: string; // 진행 상태 표시 바 색상
}

const Seekbar: React.FC<SeekbarProps> = ({
  videoRef,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  trackColor = 'grey.300',
  progressBarColor = 'primary.main',
}) => {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const progressBarRef = React.useRef<HTMLDivElement>(null);
  const thumbRef = React.useRef<HTMLDivElement>(null);
  const valueRef = React.useRef(value);
  const [isDragging, setIsDragging] = React.useState(false);

  // 마우스/터치 위치로부터 시크바의 값을 계산하는 함수
  const calculateValueFromPosition = React.useCallback(
    (clientX: number): number => {
      if (!trackRef.current) return value;

      const trackRect = trackRef.current.getBoundingClientRect();
      console.log(`${trackRect.left} ${trackRect.width}, clientX: ${clientX}`);
      const offsetX = Math.max(
        0,
        Math.min(clientX - trackRect.left, trackRect.width)
      );
      const percentage = offsetX / trackRect.width;
      const rawValue = min + percentage * (max - min);

      return rawValue;
    },
    [min, max, value]
  );

  // 드래그 시작 또는 트랙 클릭 시 호출
  const handleInteractionStart = (clientX: number) => {
    if (disabled) return;
    setIsDragging(true);
    const newValue = calculateValueFromPosition(clientX);
    // 값이 실제로 변경되었을 때만 onChange 호출
    if (newValue !== value) {
      valueRef.current = newValue;
      onChange(newValue, true);
    }
  };

  // 드래그 중 마우스/터치 이동 시 호출
  const handleInteractionMove = React.useCallback(
    (clientX: number) => {
      if (!isDragging || disabled) return;
      const newValue = calculateValueFromPosition(clientX);
      if (newValue !== value) {
        const newPercentage = (newValue / max) * 100;
        if (thumbRef.current && progressBarRef.current) {
          thumbRef.current.style.left = `calc(${newPercentage}% - ${thumbRef.current.offsetWidth / 2
            }px)`;
          progressBarRef.current.style.width = `${newPercentage}%`;
          valueRef.current = newValue;
          onChange(newValue, true);
        }
      }
    },
    [isDragging, disabled, calculateValueFromPosition, onChange, value]
  );

  // 드래그 종료 시 호출
  const handleInteractionEnd = React.useCallback(() => {
    if (disabled) return; // 비활성화 시 아무것도 안 함
    setIsDragging(false);
  }, [disabled]);

  // 전역 이벤트 리스너 등록 및 해제 (드래그 동작을 위함)
  React.useEffect(() => {
    const handleMouseMove = (event: MouseEvent) =>
      handleInteractionMove(event.clientX);
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 0) {
        handleInteractionMove(event.touches[0].clientX);
      }
    };
    const handleMouseUp = () => handleInteractionEnd();
    const handleTouchEnd = () => handleInteractionEnd();

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleTouchMove, { passive: false }); // passive: false로 스크롤 방지
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleTouchEnd);
      window.addEventListener('mouseleave', handleMouseUp); // 마우스가 창 밖으로 나갔을 때도 종료 처리
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isDragging, handleInteractionMove, handleInteractionEnd]);

  React.useEffect(() => {
    const video = videoRef?.current;
    if (video && onChange) {
      const handleTimeUpdate = () => {
        const currentVideoTime = video.currentTime;

        // 현재 Seekbar의 'value' prop과 다를 때만 onChange 호출 (무한 루프 방지)
        // 부동 소수점 비교를 위해 작은 허용 오차(epsilon)를 사용하거나 step의 절반을 기준으로 비교
        const epsilon = step > 0 ? step / 1000 : 0.00001;
        const newPercentage = (currentVideoTime / max) * 100;
        if (thumbRef.current && progressBarRef.current) {
          thumbRef.current.style.left = `calc(${newPercentage}% - ${thumbRef.current.offsetWidth / 2
            }px)`;
          progressBarRef.current.style.width = `${newPercentage}%`;
        }
        if (Math.abs(currentVideoTime - value) > epsilon) {
          valueRef.current = currentVideoTime;
          onChange(currentVideoTime, false);
        }
      };

      video.addEventListener('timeupdate', handleTimeUpdate);
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [videoRef, onChange, value, step, min, max]);
  React.useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // 트랙 자체를 클릭했을 때 값 변경
  const handleTrackClick = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (
      disabled ||
      (event.target !== trackRef.current &&
        event.target !== event.currentTarget.firstChild)
    ) {
      // Thumb 클릭 시 중복 방지
      // event.target !== event.currentTarget.firstChild : progress bar 클릭 시에도 적용되도록 수정
      if (event.target === thumbRef.current) return; // thumb 클릭 시에는 handleInteractionStart에서 처리하므로 중복 방지
    }
    const clientX =
      'touches' in event ? event.touches[0].clientX : event.clientX;
    const newValue = calculateValueFromPosition(clientX);
    valueRef.current = newValue;
    console.log('newValue', newValue);
    onChange(newValue, true);
  };

  // 키보드 접근성 처리
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    let newValue = value;
    const largeStep = (max - min) / 10; // PageUp/PageDown을 위한 큰 스텝

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = value - step;
        break;

      case 'ArrowRight':
      case 'ArrowUp':
        newValue = value + step;
        break;

      case 'Home':
        newValue = min;
        break;

      case 'End':
        newValue = max;
        break;

      case 'PageUp':
        newValue = value + (step > 0 ? Math.max(step, largeStep) : largeStep);
        break;

      case 'PageDown':
        newValue = value - (step > 0 ? Math.max(step, largeStep) : largeStep);
        break;

      default:
        return; // 다른 키는 무시
    }
    event.preventDefault(); // 기본 동작(예: 페이지 스크롤) 방지
    if (newValue !== value) {
      valueRef.current = newValue;
      onChange(newValue, true);
    }
  };

  // 현재 값에 따른 진행률(%) 계산
  const percentage =
    ((Math.max(min, Math.min(valueRef.current, max)) - min) / (max - min)) *
    100;

  const thumbSize = 0.8; // em, 핸들(thumb)의 크기
  const trackHeight = 0.3; // em, 트랙의 높이

  return (
    <Box display="flex" alignItems="center" width="100%">
      <Box
        ref={trackRef}
        onClick={handleTrackClick}
        onTouchStart={handleTrackClick} // 터치 장치에서 트랙 클릭으로 값 설정 허용
        sx={{
          position: 'relative', // 내부 요소들의 position: absolute 기준점
          width: '100%',
          height: `${trackHeight}em`,
          backgroundColor: trackColor, // 트랙 배경색
          borderRadius: `${trackHeight / 2}em`, // 둥근 트랙
          cursor: disabled ? 'not-allowed' : 'pointer',
          touchAction: 'none', // 터치 드래그 시 페이지 스크롤 방지 (중요)
          flexGrow: 1,
        }}
        // 접근성 속성
        role="slider"
        tabIndex={disabled ? -1 : 0} // 비활성화 시 포커스 불가, 활성화 시 포커스 가능
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-disabled={disabled}
        aria-label={'Seekbar'} // 라벨이 없으면 기본 라벨 제공
        onKeyDown={handleKeyDown} // 키보드 이벤트 핸들러
      >
        {/* 진행 상태 표시 바 */}
        <Box
          ref={progressBarRef}
          position="absolute"
          width={`${percentage}%`}
          height="100%"
          backgroundColor={disabled ? 'grey.400' : progressBarColor}
          borderRadius={`${trackHeight / 2}em`}
          sx={{
            position: 'absolute',
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: disabled ? 'grey.400' : 'primary.main', // 비활성화 시 색상 변경
            borderRadius: `${trackHeight / 2}px`,
            pointerEvents: 'none', // 클릭 이벤트가 트랙으로 전달되도록 함
          }}
        />
        {/* 핸들 (Thumb) */}
        <Box
          ref={thumbRef} // Thumb 참조 설정
          onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation(); // 이벤트 버블링 방지
            handleInteractionStart(e.clientX);
          }}
          onTouchStart={(e: React.TouchEvent<HTMLDivElement>) => {
            e.stopPropagation(); // 이벤트 버블링 방지
            if (e.touches.length > 0) {
              handleInteractionStart(e.touches[0].clientX);
            }
          }}
          sx={{
            position: 'absolute',
            top: '50%',
            left: `calc(${percentage}% - ${thumbSize / 2}em)`,
            width: `${thumbSize}em`,
            height: `${thumbSize}em`,
            backgroundColor: disabled ? '#bababa' : progressBarColor,
            borderRadius: '50%',
            transform: 'translateY(-50%)',
            cursor: disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.2)',
            '&:hover': {
              boxShadow: !disabled ? '0px 2px 5px rgba(0,0,0,0.3)' : undefined,
            },
            transition: isDragging
              ? 'none'
              : 'box-shadow 0.1s ease-in-out, left 0.01s linear',
            zIndex: 1,
          }}
        />
      </Box>
    </Box>
  );
};
